/**
 * "Coletar assinaturas" — lista os signatários pendentes com as duas formas de
 * assinar ali na hora: no computador da recepção ou no celular do paciente.
 *
 * Aparece logo depois de enviar o envelope no modo "Assinar no dispositivo" e
 * também pelo detalhe do envelope, para retomar uma coleta interrompida.
 */

import { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/LinkRounded';
import PhoneIcon from '@mui/icons-material/PhoneIphoneRounded';
import DrawIcon from '@mui/icons-material/GestureRounded';
import CheckIcon from '@mui/icons-material/CheckCircleRounded';

import SignOnMobileDialog from './SignOnMobileDialog';
import { getSignerLink } from '../../services/signatureService';
import { SIGNER_ROLE_LABELS, SIGNER_STATUS_LABELS } from '../../constants/signature';
import type { EnvelopeSigner, SignerLink } from '../../types/signature';

interface CollectSignaturesDialogProps {
  open: boolean;
  onClose: () => void;
  envelopeId: string;
  signers: EnvelopeSigner[];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

export default function CollectSignaturesDialog({
  open,
  onClose,
  envelopeId,
  signers,
}: CollectSignaturesDialogProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [link, setLink] = useState<SignerLink | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * O link só é pedido ao backend na hora do uso, nunca vem na listagem: cada
   * chamada registra um evento de auditoria e o token não fica circulando pela
   * tela de quem só está acompanhando o envelope.
   */
  const fetchLink = async (signerId: string): Promise<SignerLink | null> => {
    setLoadingId(signerId);
    setError(null);
    try {
      return await getSignerLink(envelopeId, signerId);
    } catch (e: any) {
      setError(e?.message || 'Não foi possível gerar o link.');
      return null;
    } finally {
      setLoadingId(null);
    }
  };

  const handleSignHere = async (signerId: string) => {
    const result = await fetchLink(signerId);
    if (result?.url) window.open(result.url, '_blank', 'noopener');
  };

  const handleSignOnMobile = async (signerId: string) => {
    setLink(null);
    setMobileOpen(true);
    const result = await fetchLink(signerId);
    setLink(result);
  };

  const handleCopy = async (signerId: string) => {
    const result = await fetchLink(signerId);
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
    } catch {
      setError(`Copie manualmente: ${result.url}`);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pr: 6 }}>
          Coletar assinaturas
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.disabled' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 3 }}>
          {error && (
            <Typography sx={{ fontSize: 13, color: 'error.main', mb: 2 }}>{error}</Typography>
          )}

          <Stack spacing={1.25}>
            {signers.map((signer) => {
              const signed = signer.status === 'signed';
              const busy = loadingId === signer.id;

              return (
                <Stack
                  key={signer.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 1.5,
                    bgcolor: signed ? 'rgba(13,148,136,0.04)' : 'background.paper',
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                    <Avatar sx={{ width: 34, height: 34, fontSize: 13, bgcolor: '#E2E8F0', color: '#334155' }}>
                      {initials(signer.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}
                        noWrap
                        title={signer.name}
                      >
                        {signer.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {SIGNER_ROLE_LABELS[signer.sign_role] || signer.sign_role}
                      </Typography>
                    </Box>
                  </Stack>

                  {signed ? (
                    <Chip
                      size="small"
                      icon={<CheckIcon />}
                      label={SIGNER_STATUS_LABELS.signed}
                      color="success"
                      variant="outlined"
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    />
                  ) : (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Copiar link pessoal">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => handleCopy(signer.id)}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Button
                        size="small"
                        startIcon={<PhoneIcon fontSize="small" />}
                        disabled={busy}
                        onClick={() => handleSignOnMobile(signer.id)}
                        sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                      >
                        Assinar no celular
                      </Button>

                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<DrawIcon fontSize="small" />}
                        disabled={busy}
                        onClick={() => handleSignHere(signer.id)}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                      >
                        Assinar
                      </Button>
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>

      <SignOnMobileDialog
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        link={link}
        loading={Boolean(loadingId) && !link}
        error={error}
      />

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Link copiado"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
